import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { ErrorPage } from "@/components/error/ErrorPage";
import { NotFoundPage } from "@/components/error/NotFoundPage";
import { CookieConsent } from "@/components/layout/CookieConsent";
import { ToastContainer } from "@/components/ui/Toast";
import { UpdatePrompt } from "@/components/ui/UpdatePrompt";
import { usePusher } from "@/hooks/use-pusher";
import { hasUserCookie } from "@/lib/auth-cookie";
import { queryKeys } from "@/lib/query-keys";
import { fetchUserProfile } from "@/lib/user-profile";
import "vanilla-cookieconsent/dist/cookieconsent.css";

export interface RouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	notFoundComponent: NotFoundPage,
	errorComponent: ErrorPage,
	beforeLoad: async ({ context }) => {
		// Die Session-Probe läuft bewusst nicht hier, sondern in den Guards von
		// _app und _setup: nur dort entscheidet der Marker über einen Redirect.
		// Die Landing-Page und /login sollen ohne Backend-Kontakt rendern.
		if (hasUserCookie()) {
			await context.queryClient
				.ensureQueryData({
					queryKey: queryKeys.user.profile(),
					queryFn: ({ signal }) => fetchUserProfile(signal),
					staleTime: 1000 * 60 * 10,
				})
				.catch(() => {
					// 401 wird vom QueryCache onError behandelt
				});
		}
	},
	component: RootLayout,
});

function RootLayout() {
	// Pusher-Verbindung lebt auf Root-Ebene, damit sie über öffentliche und
	// authentifizierte Routen hinweg bestehen bleibt. usePusher selbst no-ops, wenn
	// kein User eingeloggt ist (kein channelName), und cleanup beim echten App-Verlassen.
	usePusher();

	return (
		<>
			<Outlet />
			<ToastContainer />
			<UpdatePrompt />
			<CookieConsent />
		</>
	);
}
